import React from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";

import { makeVersionVectorTileURL, orderLayersByConfiguration } from "fond/api";
import SourceAndLayers from "fond/map/SourceAndLayers";
import { getCurrentProject } from "fond/project";
import { GroupConfig, Store } from "fond/types";
import { LayerStyle } from "fond/types/ProjectLayerConfig";
import { isVisible } from "fond/utils/configurations";

import { useOverlaysWithConfigs } from "./Overlays/useOverlaysWithConfigs";

interface IProps {
  layerView: {
    [key: string]: boolean;
  };
}

/**
 * Component responsible for rendering custom overlay layers on the map.
 * This component:
 * 1. Loads all custom overlays for the current version/project
 * 2. For each overlay, fetches its source version's configuration
 * 3. Renders each overlay's layers as vector tiles on the map
 *
 * This is separate from MapContent to keep overlay rendering independent from
 * the main project layers, allowing overlays to be displayed without affecting
 * the Legend.
 */
const OverlayMapContent: React.FC<IProps> = ({ layerView }: IProps) => {
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => getCurrentProject(state.project));

  const { overlays: mergedConfig, overlayMetadata, isLoading } = useOverlaysWithConfigs(versionId, project);

  const checkVisibility = (id: string, view: { [layerId: string]: boolean }) => {
    if (!mergedConfig) return false;
    return isVisible(mergedConfig, { id, layerView: view });
  };

  // Don't render anything if overlays are still loading or if there are no overlays
  if (isLoading || !mergedConfig) {
    return null;
  }

  // Extract styles from the merged overlay configuration
  const overlayStyles: LayerStyle[] = Object.values(mergedConfig.Data.entities).filter((entity): entity is LayerStyle => entity?.Type === "STYLE");

  // First get the parent "Overlays" group, then extract its children (the actual overlay groups)
  const rootGroup = mergedConfig.MapChildren.map((groupId: string) => mergedConfig.Data.entities[groupId]).find(
    (group): group is GroupConfig => group?.Type === "GROUP"
  );

  // The rootGroup contains a group for each overlay, which we need to extract and render
  const overlayGroups = rootGroup?.Children
    ? rootGroup.Children.map((childId: string) => mergedConfig.Data.entities[childId]).filter(
        (group): group is GroupConfig => group?.Type === "GROUP"
      )
    : [];

  return (
    <Box data-testid="overlay-map-content">
      {overlayGroups.map((group) => {
        if (!group || group.Type !== "GROUP") return null;

        // Get the overlay metadata for this group
        const metadata = overlayMetadata[group.ID];
        if (!metadata) {
          console.warn(`No metadata found for overlay group ${group.ID}`);
          return null;
        }

        const { overlayId, sourceVersionId } = metadata;

        // Get all layers that belong to this overlay group in the correct rendering order
        // orderLayersByConfiguration will recursively traverse nested groups and return
        // all layers and sublayers in the proper order for Mapbox rendering
        const groupLayers = orderLayersByConfiguration({
          ID: group.ID,
          Key: group.Key,
          SourceID: mergedConfig.SourceID || "",
          Type: "MapLayerConfig" as const,
          Data: mergedConfig.Data,
          MapChildren: group.Children || [],
        });

        if (groupLayers.length === 0) return null;

        // Create a unique source ID for this overlay group
        const sourceId = `overlay-${overlayId}`;

        return (
          <SourceAndLayers
            key={sourceId}
            sourceId={sourceId}
            source={{
              type: "vector",
              tiles: [makeVersionVectorTileURL(sourceVersionId)],
              promoteId: "id",
              maxzoom: 18,
            }}
            layers={groupLayers}
            styles={overlayStyles}
            layerVisibilities={layerView}
            isVisible={checkVisibility}
            versionId={sourceVersionId}
          />
        );
      })}
    </Box>
  );
};

export default OverlayMapContent;
